import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import { IPaginationModel } from '@/types/admin/dataTable';
import { 
  ITaskItemAttributes, 
  ITaskListResponse, 
  ITaskFilterModel, 
  ITaskSortModel,
  ITaskFormValues,
  IQuickCreateTask,
  ITaskDuplicateRequest,
  ITaskArchiveRequest,
  IAvailableAssignee,
  IArchivedTaskParams
} from '@/types/admin/tasks';

// Helper function to build query string for tasks API
const buildTasksQuery = ({
  filterModel,
  paginationModel,
  selectedPropertyId,
  searchQuery,
}: {
  filterModel?: ITaskFilterModel;
  paginationModel?: IPaginationModel;
  selectedPropertyId: number;
  searchQuery?: string;
}): URLSearchParams => {
  const searchParams = new URLSearchParams();

  // Add search query if present
  if (searchQuery && searchQuery.trim()) {
    searchParams.append('search', searchQuery.trim());
  }

  // Add filters if present
  if (filterModel) {    
    if (filterModel.creator && filterModel.creator.length > 0) {
      filterModel.creator.forEach(creator => {
        searchParams.append('creator[]', creator);
      });
    }
    
    if (filterModel.assignedTo && filterModel.assignedTo.length > 0) {
      filterModel.assignedTo.forEach(assignee => {
        searchParams.append('assignedTo[]', assignee);
      });
    }

    if (filterModel.status && filterModel.status.length > 0) {
      filterModel.status.forEach(status => {
        searchParams.append('status[]', status);
      });
    }
  }

  // Add pagination if present
  if (paginationModel && paginationModel.page > 0) {
    searchParams.append('page', paginationModel.page.toString());
    searchParams.append('pageSize', paginationModel.pageSize.toString());
  }

  // Note: Sorting is handled client-side to match admin-web behavior
  // Server-side sorting is disabled to avoid conflicts with multi-field client-side sorting

  return searchParams;
};

// Helper function to build archived tasks query
const buildArchivedTasksQuery = ({
  selectedPropertyId,
  month,
  year,
  filterModel,
  searchQuery,
}: {
  selectedPropertyId: number;
  month: number;
  year: number;
  filterModel?: ITaskFilterModel;
  searchQuery?: string;
}): URLSearchParams => {
  const searchParams = new URLSearchParams({
    month: month.toString(),
    year: year.toString(),
  });

  // Add search query if present
  if (searchQuery && searchQuery.trim()) {
    searchParams.append('search', searchQuery.trim());
  }

  // Add filters if present
  if (filterModel) {
    
    if (filterModel.creator && filterModel.creator.length > 0) {
      filterModel.creator.forEach(creator => {
        searchParams.append('creator[]', creator);
      });
    }
    
    if (filterModel.assignedTo && filterModel.assignedTo.length > 0) {
      filterModel.assignedTo.forEach(assignee => {
        searchParams.append('assignedTo[]', assignee);
      });
    }
  }

  // Note: Sorting is handled client-side to match admin-web behavior
  // Server-side sorting is disabled to avoid conflicts with multi-field client-side sorting

  return searchParams;
};

// Helper function to convert task form to API format
const convertTaskFormToApiFormat = (
  taskForm: ITaskFormValues, 
  propertyId: number,
  taskId?: number
): any => {
  // Clean subtasks - remove temporary IDs for new subtasks
  const cleanedSubtasks = (taskForm.subtasks || []).map(subtask => {
    const { id, ...subtaskWithoutId } = subtask;
    // Only include ID if it's a reasonable database ID (not a timestamp)
    if (id && id < 1000000000) {
      return subtask;
    }
    // For new subtasks (with timestamp IDs), exclude the ID
    return subtaskWithoutId;
  });

  const toUtcMidnightIso = (input: Date | string | null | undefined): string | null => {
    if (!input) return null;
    const d = new Date(input);
    if (isNaN(d.getTime())) return null;
    const utcMidnight = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0));
    return utcMidnight.toISOString();
  };

  const apiData = {
    id: taskId,
    title: taskForm.title,
    description: taskForm.description || '',
    taskStatus: taskForm.taskStatus,
    priority: taskForm.priority,
    category: taskForm.category,
    location: taskForm.location || '',
    // Normalize to midnight UTC ISO to be timezone-safe
    dueDate: toUtcMidnightIso(taskForm.dueDate),
    assignees: taskForm.assignees || [],
    subtasks: cleanedSubtasks,
    isBlocked: taskForm.isBlocked || false,
    isPrivate: taskForm.isPrivate || false,
    propertyId,
    isResidentRequest: false,
  };
  
  return apiData;
};



// RTK Query API slice for tasks
export const tasksApi = createApi({
  reducerPath: 'tasksApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Tasks', 'TaskDetails', 'ArchivedTasks', 'TaskAssignees'],
  endpoints: (builder) => ({
    // Get tasks list with filtering and pagination (sorting handled client-side)
    getTasks: builder.query<ITaskListResponse, {
      filterModel?: ITaskFilterModel;
      paginationModel?: IPaginationModel;
      selectedPropertyId: number;
      searchQuery?: string;
    }>({
      query: (params) => {
        const searchParams = buildTasksQuery(params);
        const queryString = searchParams.toString();
        const url = queryString
          ? `/api/v1/task-management/${params.selectedPropertyId}?${queryString}`
          : `/api/v1/task-management/${params.selectedPropertyId}`;
        return {
          url,
          method: 'GET',
        };
      },
      transformResponse: (response: any): ITaskListResponse => {
        
        // Handle the API response structure: { success: true, data: [...] }
        const tasksArray = response?.data || response || [];
        
        const result = {
          data: Array.isArray(tasksArray) ? tasksArray : [],
          count: Array.isArray(tasksArray) ? tasksArray.length : 0,
        };
        
        return result;
      },
      providesTags: (result, error, arg) => [
        { type: 'Tasks', id: `property-${arg.selectedPropertyId}` },
        { type: 'Tasks', id: 'LIST' },
      ],
    }),

    // Get archived tasks (sorting handled client-side)
    getArchivedTasks: builder.query<ITaskListResponse, {
      selectedPropertyId: number;
      month: number;
      year: number;
      filterModel?: ITaskFilterModel;
      searchQuery?: string;
    }>({
      query: (params) => {
        const searchParams = buildArchivedTasksQuery(params);
        const queryString = searchParams.toString();
        const url = queryString
          ? `/api/v1/task-management/${params.selectedPropertyId}/archived?${queryString}`
          : `/api/v1/task-management/${params.selectedPropertyId}/archived`;
        return {
          url,
          method: 'GET',
        };
      },
      transformResponse: (response: any): ITaskListResponse => {
        // Handle the API response structure: { success: true, data: [...] }
        const tasksArray = response?.data || response || [];
        return {
          data: Array.isArray(tasksArray) ? tasksArray : [],
          count: Array.isArray(tasksArray) ? tasksArray.length : 0,
        };
      },
      providesTags: (result, error, arg) => [
        { type: 'ArchivedTasks', id: `property-${arg.selectedPropertyId}` },
        { type: 'ArchivedTasks', id: 'LIST' },
      ],
    }),

    // Get task details by ID
    getTaskDetails: builder.query<ITaskItemAttributes, { taskId: number }>({
      query: ({ taskId }) => ({
        url: `/api/v1/task-management/details/${taskId}`,
        method: 'GET',
      }),
      transformResponse: (response: any): ITaskItemAttributes => {
        // API returns { success: boolean, data: ITaskItemAttributes }
        return (response && typeof response === 'object' && 'data' in response)
          ? (response.data as ITaskItemAttributes)
          : (response as ITaskItemAttributes);
      },
      providesTags: (result, error, { taskId }) => [
        { type: 'TaskDetails', id: taskId },
      ],
    }),

    // Create new task
    createTask: builder.mutation<ITaskItemAttributes, {
      taskForm: ITaskFormValues;
      selectedPropertyId: number;
    }>({
      queryFn: async ({ taskForm, selectedPropertyId }, api, extraOptions, baseQuery) => {
        try {
          // Step 1: Create the task without files
          const apiData = convertTaskFormToApiFormat(taskForm, selectedPropertyId);
          
          const createResult = await baseQuery({
            url: '/api/v1/task-management',
            method: 'POST',
            body: apiData,
          });

          if (createResult.error) {
            return createResult;
          }

          const createdTask = createResult.data as any;

          // Step 2: Upload files if present
          if (taskForm.files && taskForm.files.length > 0) {
            
            const formData = new FormData();
            taskForm.files.forEach((file: any, index: number) => {
              if (file && file.file) {
                const fileData = {
                  uri: file.file.uri,
                  type: file.file.type || 'application/octet-stream',
                  name: file.file.name || `file_${index}`,
                };
                formData.append('files', fileData as any);
              }
            });

            const filesResult = await baseQuery({
              url: `/api/v1/task-management/${createdTask.data.id}/files`,
              method: 'PUT',
              body: formData,
              formData: true,
            });

            if (filesResult.error) {
              console.error('❌ Failed to upload files:', filesResult.error);
              // Task was created but files failed - return the task anyway
              return { data: createdTask.data };
            }

            return { data: filesResult.data };
          }

          return { data: createdTask.data };
        } catch (error) {
          console.error('❌ Task creation failed:', error);
          return { 
            error: { 
              status: 'CUSTOM_ERROR' as const, 
              data: error,
              error: 'Task creation failed'
            } 
          };
        }
      },
      invalidatesTags: ['Tasks'],
    }),

    // Update existing task
    updateTask: builder.mutation<ITaskItemAttributes, {
      taskId: number;
      taskForm: ITaskFormValues;
      selectedPropertyId: number;
      files?: any[];
      filesToRemove?: number[];
    }>({
      queryFn: async ({ taskId, taskForm, selectedPropertyId, files, filesToRemove }, api, extraOptions, baseQuery) => {
        try {
          // Step 1: Update the task data
          const taskData = convertTaskFormToApiFormat(taskForm, selectedPropertyId, taskId);
          
          const updateResult = await baseQuery({
            url: `/api/v1/task-management/${taskId}`,
            method: 'PUT',
            body: taskData,
          });

          if (updateResult.error) {
            return updateResult;
          }

          const updatedTask = updateResult.data as any;

          // Step 2: Handle file changes if present
          // Only call files endpoint when there are actual new files to upload
          // (existing files from server should not trigger a files update request)
          const hasNewFiles = Array.isArray(files) && files.some((f: any) => f && (f as any).file);
          if (hasNewFiles || (filesToRemove && filesToRemove.length > 0)) {
            
            const formData = new FormData();
            
            // Add new files
            if (hasNewFiles) {
              files!.forEach((file: any, index: number) => {
                if (file && file.file) {
                  const fileData = {
                    uri: file.file.uri,
                    type: file.file.type || 'application/octet-stream',
                    name: file.file.name || `file_${index}`,
                  };
                  formData.append('files', fileData as any);
                }
              });
            }
            
            // Add files to remove
            if (filesToRemove && filesToRemove.length > 0) {
              formData.append('filesToRemove', JSON.stringify(filesToRemove));
            }

            const filesResult = await baseQuery({
              url: `/api/v1/task-management/${taskId}/files`,
              method: 'PUT',
              body: formData,
              formData: true,
            });

            if (filesResult.error) {
              console.error('❌ Failed to update files:', filesResult.error);
              // Task was updated but files failed - return the task anyway
              return { data: updatedTask.data };
            }

            return { data: filesResult.data };
          }

          return { data: updatedTask.data };
        } catch (error) {
          console.error('❌ Task update failed:', error);
          return { 
            error: { 
              status: 'CUSTOM_ERROR' as const, 
              data: error,
              error: 'Task update failed'
            } 
          };
        }
      },
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'TaskDetails', id: taskId },
        'Tasks',
      ],
    }),

    // Quick create task (for Kanban quick add)
    quickCreateTask: builder.mutation<ITaskItemAttributes, IQuickCreateTask>({
      query: (taskData) => ({
        url: '/api/v1/task-management',
        method: 'POST',
        body: {
          ...taskData,
          priority: 2, // Normal priority
          isPrivate: false,
          isResidentRequest: false,
          subtasks: [],
          category: 'Unassigned',
          isBlocked: false,
        },
      }),
      invalidatesTags: ['Tasks'],
    }),

    // Delete task
    deleteTask: builder.mutation<any, { taskId: number }>({
      query: ({ taskId }) => ({
        url: `/api/v1/task-management/${taskId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'TaskDetails', id: taskId },
        'Tasks',
        'ArchivedTasks',
      ],
    }),

    // Archive/Unarchive task
    archiveTask: builder.mutation<ITaskItemAttributes, ITaskArchiveRequest>({
      query: ({ taskId, isArchived, archivedAt }) => ({
        url: `/api/v1/task-management/${taskId}`,
        method: 'PUT',
        body: {
          isArchived,
          archivedAt: isArchived ? (archivedAt || new Date().toISOString()) : null,
        },
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'TaskDetails', id: taskId },
        'Tasks',
        'ArchivedTasks',
      ],
    }),

    // Duplicate task
    duplicateTask: builder.mutation<ITaskItemAttributes, { 
      taskId: number; 
      originalTask: ITaskItemAttributes; 
      selectedPropertyId: number; 
    }>({
      queryFn: async ({ taskId, originalTask, selectedPropertyId }, api, extraOptions, baseQuery) => {
        try {
          // Check if it's a resident request (cannot be duplicated)
          if (originalTask.isResidentRequest) {
            return { 
              error: { 
                status: 'CUSTOM_ERROR' as const, 
                data: 'Cannot duplicate resident requests',
                error: 'Cannot duplicate resident requests'
              } 
            };
          }

          // Create new subtasks without IDs (for new task)
          const subtasks = (originalTask.subtasks || []).map((subtask, index) => ({
            title: subtask.title,
            sortIndex: index,
            assignee: subtask.assignee || '',
            completedAt: null,
          }));

          // Create duplicate task data
          const taskData = {
            title: `(copy) ${originalTask.title}`,
            description: originalTask.description || '',
            taskStatus: originalTask.taskStatus,
            priority: originalTask.priority,
            category: originalTask.category,
            location: originalTask.location || '',
            dueDate: originalTask.dueDate || null,
            assignees: originalTask.assignees || [],
            subtasks,
            isBlocked: originalTask.isBlocked || false,
            isPrivate: false, // Reset privacy for duplicated task
            isResidentRequest: false,
            isArchived: false,
            archivedAt: null,
            propertyId: selectedPropertyId,
          };

          // Create the duplicate task using regular create endpoint
          const result = await baseQuery({
            url: '/api/v1/task-management',
            method: 'POST',
            body: taskData,
          });

          if (result.error) {
            return result;
          }

          return { data: (result.data as any)?.data || result.data };
        } catch (error) {
          console.error('❌ Task duplication failed:', error);
          return { 
            error: { 
              status: 'CUSTOM_ERROR' as const, 
              data: error,
              error: 'Task duplication failed'
            } 
          };
        }
      },
      invalidatesTags: ['Tasks'],
    }),

    // Get available assignees for property
    getAvailableAssignees: builder.query<IAvailableAssignee[], { propertyId: number }>({
      query: ({ propertyId }) => ({
        url: `/api/v1/roles/admin-slim?propertyId=${propertyId}`,
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; response: IAvailableAssignee[] }) => {
        return response.success ? response.response : [];
      },
      providesTags: (result, error, { propertyId }) => [
        { type: 'TaskAssignees', id: `property-${propertyId}` },
      ],
    }),

    // Update task files
    updateTaskFiles: builder.mutation<ITaskItemAttributes, {
      taskId: number;
      files: File[];
      filesToRemove?: number[];
    }>({
      query: ({ taskId, files, filesToRemove }) => {
        const formData = new FormData();
        
        files.forEach((file, index) => {
          formData.append(`files[${index}]`, file);
        });
        
        if (filesToRemove && filesToRemove.length > 0) {
          formData.append('filesToRemove', JSON.stringify(filesToRemove));
        }
        
        return {
          url: `/api/v1/task-management/${taskId}/files`,
          method: 'PUT',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'TaskDetails', id: taskId },
        'Tasks',
      ],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetTasksQuery,
  useLazyGetTasksQuery,
  useGetArchivedTasksQuery,
  useLazyGetArchivedTasksQuery,
  useGetTaskDetailsQuery,
  useLazyGetTaskDetailsQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useQuickCreateTaskMutation,
  useDeleteTaskMutation,
  useArchiveTaskMutation,
  useDuplicateTaskMutation,
  useGetAvailableAssigneesQuery,
  useLazyGetAvailableAssigneesQuery,
  useUpdateTaskFilesMutation,
} = tasksApi;
